// Implements: F-CNV-004, F-CNV-009, F-CNV-013, F-UIX-007, F-UIX-009
/**
 * The thread of a conversation. Pure presentation.
 *
 * The messages arrive oldest first and are drawn in that order, so the newest is
 * at the bottom. Every message names its kind and its author in TEXT (a badge and
 * a word, never colour alone) and the kind also decides the tone. The body is the
 * server's own sentence and is rendered as text: React escapes it, and nothing
 * here ever turns it into markup (threat matrix of `schemas.py`).
 *
 * A `question` (the refinement questions a turn asks instead of generating) has its own badge
 * and accent, and the thread says where to answer while it ends with one.
 *
 * An `input` and an `update` that name a version link to it. An `error` also names
 * one, but only the base it failed from, which is not a result worth a link.
 */

import type { ReactElement, ReactNode } from "react";
import { Link } from "react-router";

import { useTranslation } from "../../../shared/i18n";
import { Badge } from "../../../shared/ui/atoms/Badge";
import type { BadgeTone } from "../../../shared/ui/atoms/Badge";
import { Button } from "../../../shared/ui/atoms/Button";
import { dayText } from "../../../shared/ui/dayText";
import type { ConversationMessage, MessageKind } from "../../../types/api";
import styles from "./MessageThread.module.css";

const TONE: Readonly<Record<MessageKind, BadgeTone>> = {
  input: "neutral",
  update: "success",
  error: "danger",
  question: "info",
};

/** The address of the edit button of a message, so the screen can return the focus to it. */
export function editButtonId(messageId: string): string {
  return `conversation-edit-${messageId}`;
}

/** How much of a message names its edit button: enough to tell it from the others. */
const EXCERPT_LENGTH = 40;

function excerptOf(body: string): string {
  return body.length > EXCERPT_LENGTH ? `${body.slice(0, EXCERPT_LENGTH)}…` : body;
}

export interface MessageThreadProps {
  readonly mockupId: string;
  readonly messages: readonly ConversationMessage[];
  /** Later pages are still being read: the end of the thread is not known yet. */
  readonly loadingMore?: boolean;
  /** Called with a message of the person the person chose to edit; absent: nothing is editable now. */
  readonly onEdit?: ((message: ConversationMessage) => void) | undefined;
  /** The editor, drawn inside the entry of the message it edits. */
  readonly editor?: { readonly messageId: string; readonly node: ReactNode } | undefined;
}

export function MessageThread({
  mockupId,
  messages,
  loadingMore = false,
  onEdit,
  editor,
}: MessageThreadProps): ReactElement {
  const { t } = useTranslation();

  // The refinement questions of a turn that asked: while the thread ends with one, the person
  // is expected to answer, so say where (the composer) once, below the last of them.
  const awaitingAnswer = !loadingMore && messages.at(-1)?.kind === "question";

  return (
    <>
      <ol className={styles.thread} aria-label={t("conversation.thread.label")}>
        {messages.map((message) => (
          <li key={message.id} className={styles.message} data-kind={message.kind}>
            <span className={styles.meta}>
              <Badge tone={TONE[message.kind]}>{t(`conversation.message.kind.${message.kind}`)}</Badge>
              <span className={styles.author}>
                {message.author === "person"
                  ? t("conversation.message.person")
                  : t("conversation.message.system")}
              </span>
              <time dateTime={message.created_at}>{dayText(message.created_at)}</time>
            </span>
            {message.edit_of_message_id !== null ? (
              <p className={styles.editOf}>{t("conversation.edit.of")}</p>
            ) : null}
            <p className={styles.body}>{message.body}</p>
            {editor?.messageId === message.id ? editor.node : null}
            {onEdit !== undefined && message.kind === "input" && editor?.messageId !== message.id ? (
              <Button
                variant="ghost"
                size="sm"
                id={editButtonId(message.id)}
                label={t("conversation.edit.actionFor", { excerpt: excerptOf(message.body) })}
                onClick={() => {
                  onEdit(message);
                }}
              >
                {t("conversation.edit.action")}
              </Button>
            ) : null}
            {message.version_id !== null && message.kind !== "error" ? (
              <Link
                className={styles.link}
                to={`/mockups/${encodeURIComponent(mockupId)}/versions/${encodeURIComponent(message.version_id)}`}
              >
                {t("conversation.message.openVersion")}
              </Link>
            ) : null}
          </li>
        ))}
      </ol>
      {awaitingAnswer ? <p className={styles.answerHint}>{t("conversation.thread.answerHint")}</p> : null}
    </>
  );
}
